import React, { useEffect, useState } from "react";
import InputGroup from "../InputGroup";
import {Alert, Button, Tab, TextInput} from "omoplay";
import GrayButton from "../../GrayButton";
import CheckCircleIcon from "../../../assets/images/icon.check.green.svg"
import DisabledCheckCircleIcon from "../../../assets/images/check_circle_disabled_icon.svg"
import EventLoginPreview from "./EventLoginPreview";
import EventRoulettePreview from "./EventRoulettePreview";
import EventPrizePreview from "./EventPrizePreview";
import ImageForm from "../../ImageForm";
import RouletteGenerateForm from "../../RouletteGenerateForm";
import RouletteButtonGenerateForm from "../../RouletteButtonGenerateForm";
import BackgroundImageGenerateForm from "../../BackgroundImageGenerateForm";
import FriendsTempPage from "../../FriendsTempPage";
import {Sparkles, Ticket, X} from "lucide-react";
import NoImage from "../../../assets/images/no-image.svg";
import {generateEvent} from "../../../api/ai/ai.api";

const AIModal = ({ isOpen, name, benefitSettings, onClose, onConfirm, onBenefitChange }) => {
    const [prompt, setPrompt] = useState("")

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
                {/* Header */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-gray-800">AI 이미지 생성</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="divide-y divide-gray-100">
                    <div className="flex-center px-4 py-6">
                        <h3 className="flex-1 text-lg font-bold text-gray-800 px-4">반영될 이벤트 이름</h3>
                        <div className="font-semibold text-lg">{name}</div>
                    </div>
                    <div className="flex-center px-4 py-6">
                        <h3 className="flex-1 text-lg font-bold text-gray-800 px-4">반영될 혜택 리스트</h3>
                        <button
                            onClick={onBenefitChange}
                            className="flex-1 py-2 px-1 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 shadow-md transform transition active:scale-95"
                        >
                            혜택 수정하러 가기
                        </button>
                    </div>
                    {benefitSettings?.map((benefit) => (
                        <div key={benefit.id} className="p-6 md:grid md:grid-cols-12 md:gap-6 md:items-start hover:bg-gray-50 transition-colors group">

                            {/* 1. 혜택 정보 & 메시지 설정 */}
                            <div className="col-span-4 space-y-3 mb-4 md:mb-0">
                                <div className="flex items-start gap-3">
                                    <div className="w-16 h-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-100 shrink-0">
                                        <img
                                            src={benefit.prizeImage ?? NoImage}
                                            alt="혜택 이미지"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-sm font-bold text-gray-900 truncate">{benefit.name}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* 데이터 없음 처리 */}
                    {(!benefitSettings || benefitSettings.length === 0) && (
                        <div className="py-12 text-center">
                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                                <Ticket className="w-8 h-8 text-gray-300" />
                            </div>
                            <p className="text-gray-500 text-sm">등록된 혜택이 없습니다.</p>
                        </div>
                    )}
                </div>
                <div className="px-6 py-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        생성 요구사항(선택)
                    </label>
                    <input
                        type="text"
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder="예: 한식 프랜차이즈, 밝은 분위기"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                    />
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 px-4 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                    >
                        취소
                    </button>
                    <button
                        onClick={() => onConfirm(prompt)}
                        className="flex-1 py-3 px-4 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 shadow-md transform transition active:scale-95"
                    >
                        생성하기
                    </button>
                </div>
            </div>
        </div>
    );
};

const EventScreen = ({
    event, onChange, onValid, goBenefit
}) => {
    const eventType = event.type ?? "roulette"
    const loginBackgroundImage = event.loginBackgroundImage ?? ""
    const eventBackgroundImage = event.eventBackgroundImage ?? ""
    const eventImage = event.eventImage ?? ""
    const eventButtonImage = event.eventButtonImage ?? (event.eventImage ? "https://img.headless.co.kr/roulette/start_image.svg" : "")
    const loginButtonName = event.loginButtonName ?? "로그인하고 룰렛 돌리기"
    const loginPrecautionName = event.loginPrecautionName ?? ""
    const loginPrecautionContent = event.loginPrecautionContent ?? ""
    const prizePrecautionContent = event.prizePrecautionContent
    const additionalButtons = event.additionalButtons ?? []
    const [detailStep, setDetailStep] = useState("")
    const [notRequired, setNotRequired] = useState(false)
    const helpUrl = "https://www.figma.com/design/qUhYdZoiQzKfq9Y8NhzN3a/-OMO-PLAY--Event-Templates?node-id=0-1&t=xzafcO45CFFG8Iln-1"
    const [prompt, setPrompt] = useState("")
    const [showAi, setShowAi] = useState(false)

  useEffect(() => {
    if (eventType === "roulette" || eventType === "skip") {
      setDetailStep("login")
    }
  }, []);

  useEffect(() => {
    let valid

    if (eventType === "roulette") {
      valid = loginBackgroundImage && loginButtonName && eventBackgroundImage && eventImage && prizePrecautionContent
    } else if (eventType === "skip") {
      valid = loginBackgroundImage && loginButtonName && prizePrecautionContent
    } else if (eventType === "friends") {
      valid = true
    } else if (eventType === "lottery") {
      valid = loginBackgroundImage && loginButtonName
    }
    setNotRequired(valid)
    onValid(valid)
  }, [loginBackgroundImage, loginButtonName, eventBackgroundImage, eventImage, eventButtonImage, detailStep, prizePrecautionContent])

  let registrationTypeSwitchItems = []

  if (eventType === "roulette") {
    registrationTypeSwitchItems = [
      {
        label: (
            <div className="flex-center-all gap-2"><img alt="" src={(loginBackgroundImage && loginButtonName) ? CheckCircleIcon : DisabledCheckCircleIcon} /><div>로그인 설정</div></div>
        ),
        value: "login",
        style: {flex: 1},
        selected: true
      },
      {
        label: (
            <div className="flex-center-all gap-2"><img alt="" src={(eventBackgroundImage && eventImage) ? CheckCircleIcon : DisabledCheckCircleIcon} /><div>룰렛 설정</div></div>
        ),
        value: "roulette",
        style: {flex: 1},
      },
      {
        label: (
            <div className="flex-center-all gap-2"><img alt="" src={(prizePrecautionContent) ? CheckCircleIcon : DisabledCheckCircleIcon} /><div>당첨 안내 설정</div></div>
        ),
        value: "prize",
        style: {flex: 1},
      }
    ]
  } else if (eventType === "skip") {
    registrationTypeSwitchItems = [
      {
        label: (
            <div className="flex-center-all gap-2"><img alt="" src={(loginBackgroundImage && loginButtonName) ? CheckCircleIcon : DisabledCheckCircleIcon} /><div>로그인 설정</div></div>
        ),
        value: "login",
        selected: true,
        style: {flex: 1},
      },
      {
        label: (
            <div className="flex-center-all gap-2"><img alt="" src={(prizePrecautionContent) ? CheckCircleIcon : DisabledCheckCircleIcon} /><div>당첨 안내 설정</div></div>
        ),
        value: "prize",
        style: {flex: 1},
      }
    ]
  } else if (eventType === "friends") {
    registrationTypeSwitchItems = []
  }

  const detailStepRender = () => {

    if (detailStep === "login") {
      return {
        settings: (
            <div className="flex-col gap-8">
              <div className="flex-align-center justify-between">
                <div className="font-bold">1. 로그인 설정</div>
                <div className="bg-mainblue-l4 radius-6 p-[5px,14px]" style={{padding: "5px 14px"}}>
                  <div className="text-primary font-12px underline pointer"
                       onClick={() => window.open(helpUrl)}>
                    제작 가이드 (피그마)
                  </div>
                </div>
              </div>
                <div>
                    <label className="text-sm font-semibold text-gray-900 relative flex items-center gap-2 mb-2">
                        메인 이미지
                        <span className="text-red-500">*</span>
                    </label>
                    <ImageForm imageUrl={loginBackgroundImage} onChangeImageUrl={(url) => onChange("loginBackgroundImage", url)}
                               sizeInfo={"권장 사이즈 : 1000 X 1400px"} prompt={prompt}
                    >
                    </ImageForm>
                </div>
              <div>
                <div className="flex items-center mb-1">
                  <label className="text-sm font-semibold text-gray-900 relative flex items-center gap-2">
                    로그인 버튼 설정
                    <span className="text-red-500">*</span>
                  </label>
                </div>
                <div className="relative">
                  <input
                      type="text"
                      className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2 "
                      value={
                          loginButtonName ?? "로그인하고 룰렛 돌리기"
                      }
                      onChange={(e) => onChange("loginButtonName", e.target.value)}
                      maxLength={20}
                      placeholder="버튼명을 입력해 주세요."
                      required
                  />
                  <span className="absolute end-3 bottom-3 text-xs text-gray-400">
                {loginButtonName?.length || 0}/20자
              </span>
                </div>
              </div>
              <div>
                <div className="flex items-center mb-1">
                  <label className="text-sm font-semibold text-gray-900 relative flex items-center gap-2">
                    이벤트 안내항목 제목
                  </label>
                </div>
                <div className="relative">
                  <input
                      type="text"
                      className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2 "
                      value={loginPrecautionName || ""}
                      onChange={(e) =>
                          onChange("loginPrecautionName", e.target.value)
                      }
                      maxLength={20}
                      placeholder="이벤트 유의사항"
                  />
                  <span className="absolute end-3 bottom-3 text-xs text-gray-400">
                {loginPrecautionName?.length || 0}/20자
              </span>
                </div>
              </div>
              <div>
                <div className="flex items-center mb-1">
                  <label className="text-sm font-semibold text-gray-900 relative flex items-center">
                    이벤트 안내항목 내용
                  </label>
                </div>
                <p className="text-gray-500 text-sm mb-2">
                  고객이 알아야 할 행사 및 이벤트의 내용을 입력해 주세요.
                </p>
                <div className="relative">
              <textarea
                  className="bg-white border border-gray-300 text-gray-900 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2  resize-none text-xs whitespace-pre-line"
                  onChange={(e) =>
                      onChange("loginPrecautionContent", e.target.value)
                  }
                  rows={10}
                  placeholder="[예시]&#13;&#10;
                · 본 행사는 000 회원만 참여 가능하며 콘테스트 기간 2000.00.00 ~ 2000.00.00 내 참여한 회원에 한해 응모 및 당첨 가능합니다.
                기간 내 1인 당 하나의 ID만 참여 가능합니다.&#13;&#10;
                · 상기 상품 이미지는 이해를 돕기 위해 연출된 것으로 실제 상품과 다를 수 있습니다.&#13;&#10;
                · 본 행사 내용은 당사 사정에 따라 예고 없이 변경 또는 중단될 수 있습니다.&#13;&#10;
                · 행사 관련 문의 : 고객센터 0000-0000 또는 카카오톡플러스친구 ‘0000’으로 문의주세요."
                  value={loginPrecautionContent}
              ></textarea>
                </div>
              </div>
            </div>
        ),
        preview: (
            <EventLoginPreview loginBackgroundImage={loginBackgroundImage}
                               loginButtonName={loginButtonName}
                               loginPrecautionContent={loginPrecautionContent}
                               loginPrecautionName={loginPrecautionName}
            />
        )
      }
    } else if (detailStep === "roulette") {
      return {
        settings: (
            <div className="flex-col gap-8">
              <div className="flex-align-center justify-between">
                <div className="font-bold">2. 룰렛 설정<span className="text-red-500">*</span></div>
                <div className="bg-mainblue-l4 radius-6" style={{padding: "5px 14px"}}>
                  <div className="text-primary font-12px underline pointer"
                       onClick={() => window.open(helpUrl)}>
                    제작 가이드 (피그마)
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center mb-1">
                  <label className="text-sm font-semibold text-gray-900 relative flex items-center gap-2">
                    배경 이미지
                    <span className="text-red-500">*</span>
                  </label>
                </div>
                <ImageForm imageUrl={eventBackgroundImage} onChangeImageUrl={(url) => onChange("eventBackgroundImage", url)}
                           sizeInfo={"권장 사이즈 : 1000 X 1700px"} showAIButton={false} prompt={prompt}
                >
                  <BackgroundImageGenerateForm onPrompt={setPrompt} />
                </ImageForm>
              </div>
              <div>
                <div className="flex items-center mb-1">
                  <label className="text-sm font-semibold text-gray-900 relative flex items-center gap-2">
                    룰렛 이미지
                    <span className="text-red-500">*</span>
                  </label>
                </div>
                <ImageForm imageUrl={eventImage} onChangeImageUrl={(url) => onChange("eventImage", url)}
                           sizeInfo={"권장 사이즈 : 580 X 580px"} showAIButton={false} prompt={prompt}
                >
                  <RouletteGenerateForm onPrompt={setPrompt} />
                </ImageForm>
              </div>
              <div className="flex-col gap-2">
                <div className="flex items-center">
                  <label className="text-sm font-semibold text-gray-900 relative flex items-center gap-2">
                    룰렛 버튼
                  </label>
                </div>
                <ImageForm imageUrl={eventButtonImage} onChangeImageUrl={(url) => onChange("eventButtonImage", url)}
                           sizeInfo={"권장 사이즈 : 172 X 172px"} showAIButton={false} prompt={prompt}
                >
                  <RouletteButtonGenerateForm onPrompt={setPrompt} />
                </ImageForm>
                <p className="text-xs text-primary">
                  * 별도의 룰렛 버튼을 업로드 하지 않을 시 기본 버튼으로 제공됩니다.
                </p>
              </div>
            </div>
        ),
        preview: (
            <EventRoulettePreview eventBackgroundImage={eventBackgroundImage}
                                  eventImage={eventImage}
                                  eventButtonImage={eventButtonImage}
            />
        )
      }
    } else if (detailStep === "prize") {
      return {
        settings: (
            <div className="flex-col gap-8">
              <div className="flex-align-center justify-between">
                <div className="font-bold">3. 당첨 안내 설정<span className="text-red-500">*</span></div>
                <div className="bg-mainblue-l4 radius-6" style={{padding: "5px 14px"}}>
                  <div className="text-primary font-12px underline pointer"
                       onClick={() => window.open(helpUrl)}>
                    제작 가이드 (피그마)
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center mb-1">
                  <label className="text-sm font-semibold text-gray-900 relative flex items-center gap-2">
                    <div>당첨 안내 문구</div>
                    <span className="text-red-500">*</span>
                  </label>
                </div>
                <p className="text-gray-500 text-sm mb-2">
                  당첨 혜택 사용에 필요한 문구를 설정해 주세요.
                </p>
                <div className="relative">
                    <textarea
                        className="bg-white border border-gray-300 text-gray-900 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2  resize-none text-xs whitespace-pre-line"
                        onChange={(e) =>
                            onChange("prizePrecautionContent", e.target.value)
                        }
                        maxLength={40}
                        rows={5}
                        value={prizePrecautionContent}
                    ></textarea>
                  <span className="absolute end-3 bottom-3 text-xs text-gray-400">
                      {prizePrecautionContent?.length || 0}/40자
                    </span>
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900 relative flex items-center gap-2">
                  추가 유도 설정
                </div>
                <div className="text-gray-500 text-sm mb-2">
                  추가 유도 버튼을 설정해 보세요. (최대 2개, 최소 0개)
                </div>
                <div className="flex-col gap-4">
                  {
                    additionalButtons.map((button, index) => {
                      return (
                          <div className="flex-col gap-2 bg-grey-0 rounded" style={{padding: "12px"}}>
                            <div className="flex justify-between">
                              <div>
                                버튼
                              </div>
                              <div>
                                <GrayButton className="px-1.5" onClick={() => {
                                  onChange("additionalButtons", additionalButtons.filter((button, buttonIndex) => buttonIndex !== index))
                                }}>
                                  X
                                </GrayButton>
                              </div>
                            </div>
                            <div>
                              <TextInput value={button.name} onChange={(e) => {
                                const changeButtons = [...additionalButtons]
                                const currentButton = changeButtons.find((button, buttonIndex) => buttonIndex === index)
                                currentButton.name = e.target.value
                                onChange("additionalButtons", changeButtons)
                              }} maxLength={14} />
                            </div>
                            <div className="flex gap-4">
                              <div style={{width: "100%"}}>
                                <TextInput value={button.url} onChange={(e) => {
                                  const changeUrl = [...additionalButtons]
                                  const currentButton = changeUrl.find((button, buttonIndex) => buttonIndex === index)
                                  currentButton.url = e.target.value
                                  onChange("additionalButtons", changeUrl)
                                }} />
                              </div>
                              <Button style={{flexBasis: "30%"}} variants="default" value="URL 확인" onClick={() => window.open(button.url)} />
                            </div>
                          </div>
                      )
                    })
                  }
                  <div>
                    <Button variants="outlined" value="+ 버튼 추가" disabled={additionalButtons.length >= 2} onClick={() => {
                      if (additionalButtons.length <= 2) {
                        onChange("additionalButtons", [...additionalButtons, {
                          name: "",
                          url: ""
                        }])
                      }
                    }} />
                  </div>
                </div>
              </div>
            </div>
        ),
        preview: (
            <EventPrizePreview eventBackgroundImage={eventBackgroundImage}
                               eventImage={eventImage}
                               eventButtonImage={eventButtonImage}
                               prizePrecautionContent={prizePrecautionContent}
                               additionalButtons={additionalButtons}
            />
        )
      }
    } else if (eventType === "friends") {
      return {
        settings: (<>
          <div>친구 추가 이벤트는 아직 설정이 존재하지 않습니다.</div>
                </>),
        preview: (<FriendsTempPage />)
      }
    } else if (eventType === "lottery") {
      return {
        settings: (
            <div className="flex-col gap-8">
              <div className="flex-align-center justify-between">
                <div className="font-bold">로그인 설정</div>
                <div className="bg-mainblue-l4 radius-6" style={{padding: "5px 14px"}}>
                  <div className="text-primary font-12px underline pointer"
                       onClick={() => window.open(helpUrl)}>
                    제작 가이드 (피그마)
                  </div>
                </div>
              </div>
              <ImageForm imageUrl={loginBackgroundImage} onChangeImageUrl={(url) => onChange("loginBackgroundImage", url)}
                         sizeInfo={"권장 사이즈 : 370 X 370px"} prompt={prompt}
              >
              </ImageForm>
              <div>
                <div className="flex items-center mb-1">
                  <label className="text-sm font-semibold text-gray-900 relative flex items-center gap-2">
                    로그인 버튼 설정
                    <span className="text-red-500">*</span>
                  </label>
                </div>
                <div className="relative">
                  <input
                      type="text"
                      className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2 "
                      value={
                          loginButtonName ?? "로그인하고 룰렛 돌리기"
                      }
                      onChange={(e) => onChange("loginButtonName", e.target.value)}
                      maxLength={20}
                      placeholder="버튼명을 입력해 주세요."
                      required
                  />
                  <span className="absolute end-3 bottom-3 text-xs text-gray-400">
                {loginButtonName?.length || 0}/20자
              </span>
                </div>
              </div>
              <div>
                <div className="flex items-center mb-1">
                  <label className="text-sm font-semibold text-gray-900 relative flex items-center gap-2">
                      이벤트 안내항목 제목
                  </label>
                </div>
                <div className="relative">
                  <input
                      type="text"
                      className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2 "
                      value={loginPrecautionName || ""}
                      onChange={(e) =>
                          onChange("loginPrecautionName", e.target.value)
                      }
                      maxLength={20}
                      placeholder="이벤트 유의사항"
                  />
                  <span className="absolute end-3 bottom-3 text-xs text-gray-400">
                {loginPrecautionName?.length || 0}/20자
              </span>
                </div>
              </div>
              <div>
                <div className="flex items-center mb-1">
                  <label className="text-sm font-semibold text-gray-900 relative flex items-center">
                      이벤트 안내항목 내용
                  </label>
                </div>
                <p className="text-gray-500 text-sm mb-2">
                  고객이 알아야 할 행사 및 이벤트의 내용을 입력해 주세요.
                </p>
                <div className="relative">
              <textarea
                  className="bg-white border border-gray-300 text-gray-900 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2  resize-none text-xs whitespace-pre-line"
                  onChange={(e) =>
                      onChange("loginPrecautionContent", e.target.value)
                  }
                  rows={10}
                  placeholder="[예시]&#13;&#10;
                · 본 행사는 000 회원만 참여 가능하며 콘테스트 기간 2000.00.00 ~ 2000.00.00 내 참여한 회원에 한해 응모 및 당첨 가능합니다.
                기간 내 1인 당 하나의 ID만 참여 가능합니다.&#13;&#10;
                · 상기 상품 이미지는 이해를 돕기 위해 연출된 것으로 실제 상품과 다를 수 있습니다.&#13;&#10;
                · 본 행사 내용은 당사 사정에 따라 예고 없이 변경 또는 중단될 수 있습니다.&#13;&#10;
                · 행사 관련 문의 : 고객센터 0000-0000 또는 카카오톡플러스친구 ‘0000’으로 문의주세요."
                  value={loginPrecautionContent}
              ></textarea>
                </div>
              </div>
            </div>
        ),
        preview: (
            <EventLoginPreview loginBackgroundImage={loginBackgroundImage}
                               loginButtonName={loginButtonName}
                               loginPrecautionContent={loginPrecautionContent}
                               loginPrecautionName={loginPrecautionName}
            />
        )
      }
    } else {
        return {
            settings: <></>,
            preview: <></>,
        }
    }
  }

  return (
      <>
        <InputGroup label={"이벤트 설정"}>
          <div className="flex-col gap-2">
              <button
                  onClick={() => setShowAi(true)}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-indigo-200 transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
              >
                  <Sparkles className="w-5 h-5" />
                  AI 이미지 자동 생성
              </button>
            <div className="w-full">
              <Tab variants="input" items={registrationTypeSwitchItems}
                   onChanged={(item) => {
                     setDetailStep(item.value)
                     if (item.value === "prize" && !prizePrecautionContent) {
                       onChange("prizePrecautionContent", "카카오톡으로 쿠폰 발송 완료!\n" +
                           "결제 시 사용해 주세요.")
                     }
                   }} styles={{width: "100%"}} />
            </div>
            {
              notRequired ? (
                  <Alert title="모든 설정이 완료되었습니다" type="info" withIcon={true} />
              ) : (
                  <Alert title="모든 단계의 필수 설정이 완료되지 않았습니다" type="error" withIcon={true} />
              )
            }
          </div>
          <div className="flex gap-8" key={detailStep}>
            <div style={{flexBasis: "50%", paddingTop: "34px"}}>
              {
                detailStepRender().settings
              }
            </div>
            <div className="bg-gray-50 flex-center-all flex-col" style={{paddingTop: "30px", paddingBottom: "20px"}}>
              {
                detailStepRender().preview
              }
              <div style={{color: "var(--red-0)", padding: "20px 60px"}}>
                <div>* 위 화면은 정확한 위치와 차이가 있어,</div>
                <div>프로젝트에서 진입점을 연결한 후 직접 확인해 보시길 바랍니다.</div>
              </div>
            </div>
          </div>
        </InputGroup>
          <AIModal isOpen={showAi}
                   name={event.name}
                   benefitSettings={event.benefitSettings}
                   onClose={() => setShowAi(false)}
                   onBenefitChange={goBenefit}
                   onConfirm={(prompt) => {
                       generateEvent(event.id, event.name, event.benefitSettings.map(b => b.name), prompt).then(() => {
                           window.location.reload()
                       }).catch((e) => {
                           // AI 생성 실패(503) — 사유 alert. 종전엔 500 → /notfound 튕김.
                           alert(e?.message || "AI 이미지 생성에 실패했습니다. 잠시 후 다시 시도해 주세요.");
                       })
                   }}
          />
      </>
  )
};

export default EventScreen;
